
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { toast as sonnerToast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { sendOrderParamsToBackend } from '@/services/orderService';
import { getPlanById } from '@/config/pricingPlans';
import { ShieldCheck, AlertCircle } from 'lucide-react';

interface CashfreeDropInCheckoutProps {
  amount: number;
  productName: string;
  customerName: string;
  customerEmail: string;
  customerPhone?: string;
  onSuccess?: (data: any) => void;
  onFailure?: (error: any) => void;
  planId?: string;
}

const CashfreeDropInCheckout: React.FC<CashfreeDropInCheckoutProps> = ({
  amount,
  productName,
  customerName,
  customerEmail,
  customerPhone = '',
  onSuccess,
  onFailure,
  planId = 'basic'
}) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const orderId = urlParams.get('order_id');
    const orderStatus = urlParams.get('order_status');
    const plan = urlParams.get('plan');
    
    if (orderId) {
      console.log("Found order_id in URL:", orderId, "with status:", orderStatus || "unknown");
      
      if (orderId) {
        console.log("Sending order_id to backend for processing:", orderId);
        supabase.functions.invoke('create-payment-order', {
          body: {
            urlParams: {
              order_id: orderId,
              plan: plan || planId,
              status: orderStatus || 'PENDING',
              productName: plan ? 
                plan.toLowerCase() === 'professional' ? 'Professional Plan' :
                plan.toLowerCase() === 'starter' ? 'Starter Plan' : 'Basic Plan'
                : productName,
              email: customerEmail,
              name: customerName,
              phone: customerPhone
            }
          }
        }).then(response => {
          console.log("Response from backend for URL parameters:", response);
        }).catch(err => {
          console.error("Error sending URL parameters to backend:", err);
        });
      }
      
      if (!orderStatus) {
        fetchOrderStatus(orderId);
      } else if (orderStatus === 'SUCCESS' || orderStatus === 'PAID' || orderStatus === 'ACTIVE' || orderStatus === 'DIRECT') {
        handleSuccessfulPayment(orderId, plan);
      } else {
        handleFailedPayment(orderStatus || 'UNKNOWN', orderId);
      }
    }
  }, [onSuccess, onFailure, navigate, customerEmail, productName, planId, customerName, customerPhone]);

  const fetchOrderStatus = async (orderId: string) => {
    try {
      console.log("Manually checking payment status for order:", orderId);
      
      const { data, error } = await supabase
        .from('payment_orders')
        .select('status, product_name')
        .eq('order_id', orderId)
        .maybeSingle();
      
      if (error) {
        console.error("Error fetching order status from database:", error);
      } else if (data) {
        console.log("Found order in database with status:", data.status);
        
        if (data.status === 'PAID') {
          handleSuccessfulPayment(orderId, data.product_name?.toLowerCase().includes('professional') ? 'professional' : 
                                      data.product_name?.toLowerCase().includes('starter') ? 'starter' : 'basic');
          return;
        } else if (data.status !== 'PENDING') {
          handleFailedPayment(data.status || 'UNKNOWN', orderId);
          return;
        }
      }
      
      console.log("Calling API to check payment status for order:", orderId);
      const result = await supabase.functions.invoke('create-payment-order', {
        body: {
          checkPaymentStatus: true,
          orderId: orderId,
          fromThankYouPage: true
        }
      });
      
      console.log("Payment status check result:", result);
      
      if (result.data?.data?.status === 'PAID') {
        handleSuccessfulPayment(orderId, 
          result.data.data.product_name?.toLowerCase().includes('professional') ? 'professional' : 
          result.data.data.product_name?.toLowerCase().includes('starter') ? 'starter' : 'basic');
      } else if (result.data?.data?.status === 'FAILED') {
        handleFailedPayment(result.data.data.status, orderId);
      }
    } catch (err) {
      console.error("Exception checking payment status:", err);
    }
  };

  const handleSuccessfulPayment = (orderId: string, planType?: string | null) => {
    sonnerToast.success("Payment successful!");
    
    sessionStorage.setItem('last_order_id', orderId);
    if (customerEmail) sessionStorage.setItem('customer_email', customerEmail);
    
    const selectedPlan = planType || sessionStorage.getItem('selected_plan') || 'basic';
    sessionStorage.setItem('purchased_plan', selectedPlan);
    
    if (onSuccess) onSuccess({ orderId });
    
    navigate(`/thank-you?order_id=${orderId}&plan=${selectedPlan}`);
  };

  const handleFailedPayment = (status: string, orderId: string) => {
    const errorMessage = `Payment ${status.toLowerCase()}`;
    sonnerToast.error(errorMessage);
    if (onFailure) onFailure({ message: errorMessage, orderId });
  };

  const handlePaymentClick = async () => {
    if (!customerName || !customerEmail) {
      toast({
        title: "Missing Information",
        description: "Please provide your name and email to continue",
        variant: "destructive"
      });
      return;
    }

    try {
      setLoading(true);
      setError(null);
      sonnerToast.info("Initializing payment...");

      const newOrderId = `order_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
      
      // Make sure we're using the correct planId
      const planType = planId;
      
      console.log(`Starting payment process with:
        - Product Name: "${productName}"
        - Amount: ${amount}
        - Plan ID: ${planType}
        - Customer: ${customerName} (${customerEmail})
      `);
      
      // Verify that we're using the correct amount from the plan
      const planDetails = getPlanById(planType);
      if (planDetails.monthlyAmount !== amount) {
        console.warn(`Amount mismatch: Received ${amount} but plan ${planType} should be ${planDetails.monthlyAmount}`);
        // Use the correct amount from plan config
        amount = planDetails.monthlyAmount;
        console.log(`Corrected payment amount to: ${amount}`);
      }
      
      // Store selected plan and customer data in session
      sessionStorage.setItem('selected_plan', planType);
      sessionStorage.setItem('customer_name', customerName);
      sessionStorage.setItem('customer_email', customerEmail);
      if (customerPhone) sessionStorage.setItem('customer_phone', customerPhone);
      
      // First store the order params in the backend to ensure we have a record
      await sendOrderParamsToBackend({
        order_id: newOrderId,
        plan: planType,
        status: 'INITIATED',
        email: customerEmail,
        name: customerName,
        phone: customerPhone
      });
      
      // Then create the actual payment link
      const payload = {
        amount: Number(amount),
        orderId: newOrderId,
        planType: planType,
        productName: productName,
        customerDetails: {
          customerId: `customer_${Date.now()}`.substring(0, 20),
          phone: customerPhone || '',
          name: customerName,
          email: customerEmail
        }
      };

      console.log("Creating payment link with payload:", JSON.stringify(payload, null, 2));
      
      const response = await supabase.functions.invoke('create-payment-order', {
        body: payload
      });
      
      console.log("Response from Supabase:", response);

      if (response.error) {
        console.error("Supabase function error:", response.error);
        throw new Error(`Failed to create payment link: ${response.error.message || response.error}`);
      }
      
      const data = response.data;

      if (!data?.success) {
        console.error("Payment link creation failed:", data?.error);
        throw new Error(data?.error || "Failed to create payment link");
      }

      if (!data.data?.link_url) {
        throw new Error("No payment link received from server");
      }

      // Check if this is the account setup needed case
      if (data.data.account_setup_needed) {
        console.log("Account setup needed. Using direct flow.");
        setError("Cashfree account setup needed. Please contact support or use an alternative payment method.");
        
        // Still allow navigation to thank you page for direct handling
        setTimeout(() => {
          window.location.href = data.data.link_url;
        }, 3000);
        
        return;
      }

      sessionStorage.setItem('last_order_id', newOrderId);
      
      if (data.data.product_name) {
        sessionStorage.setItem('product_name', data.data.product_name);
      }

      console.log("Redirecting to payment link:", data.data.link_url);
      window.location.href = data.data.link_url;
    } catch (error: any) {
      console.error("Payment link creation error:", error);
      setLoading(false);
      setError(error.message || "Failed to create payment link. Please try again later.");
      toast({
        title: "Error",
        description: error.message || "Failed to create payment link. Please try again later.",
        variant: "destructive"
      });
      sonnerToast.error("Payment initialization failed");
      if (onFailure) onFailure(error);
    }
  };

  return (
    <div className="space-y-6 w-full">
      <Button
        onClick={handlePaymentClick}
        className="w-full"
        disabled={loading}
        variant="default"
      >
        {loading ? "Initializing..." : `Pay ₹${amount.toFixed(2)}`}
      </Button>
      
      {error && (
        <div className="rounded-md bg-yellow-50 p-4 border border-yellow-200">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertCircle className="h-5 w-5 text-yellow-400" aria-hidden="true" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">Payment setup required</h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>{error}</p>
                <p className="mt-2">The payment will proceed in demonstration mode. In a production environment, please make sure your payment gateway is properly configured.</p>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div className="rounded-md p-3 bg-blue-50 border border-blue-100">
        <div className="flex items-start">
          <ShieldCheck className="h-5 w-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-blue-800">Your data is secure</h4>
            <p className="text-xs text-blue-700">We use industry-standard encryption to protect your personal and payment information. We never store your full payment details on our servers.</p>
          </div>
        </div>
        <div className="flex items-center space-x-4 mt-2">
          <div className="flex items-center">
            <img src="https://logowik.com/content/uploads/images/cashfree-payments7934.logowik.com.webp" alt="Cashfree Payments" className="h-8 mr-2" />
            <img src="https://cashfreelogo.cashfree.com/assets_images/marketing/pcidss.svg" alt="PCI Compliant" className="h-6" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CashfreeDropInCheckout;
